import { useQuery } from '@tanstack/react-query';

import { listExecutionImages } from '@/lib/api/generated/api';
import type { ExecutionImageView } from '@/lib/api/generated/model';

import { executionImageKeys } from '../queryKeys';

/**
 * Every execution image in the workspace's catalog — the AISDLC-provided
 * image plus any custom images added from a connected registry (FR-001).
 *
 * Same hand-written-`queryFn` convention as `useConnections`: calls the
 * generated raw `listExecutionImages` fetcher directly, not the generated
 * `useListExecutionImages` hook — no feature in this codebase actually
 * consumes orval's generated query hooks.
 *
 * Anything but a 200 throws rather than resolving to an empty list — a
 * workspace with images already catalogued must not look like one with none
 * after a failed load.
 */
export function useExecutionImages(workspaceId: string) {
  const query = useQuery({
    queryKey: executionImageKeys.list(workspaceId),
    queryFn: async (): Promise<ExecutionImageView[]> => {
      const response = await listExecutionImages(workspaceId);
      if (response.status !== 200) {
        throw new Error('The execution images could not be loaded.');
      }
      return response.data;
    },
    retry: false,
  });

  return {
    images: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
  };
}
